/**
 * Internal contract interaction helpers
 */
import type { Address, Chain, Client, Transport, WalletClient } from "viem"
import { BaseError, ContractFunctionRevertedError } from "viem"
import { readContract } from "viem/actions"
import { breakoutMarketAbi } from "../abi/generated/breakoutMarket"
import { Errors } from "../errors"
import type { MarketStatus } from "../types"
import { fromTimestamp } from "./convert"

// =============================================================================
// Common Options for Write Actions
// =============================================================================

/**
 * Common options for SDK write actions
 */
export interface WriteOptions {
	/**
	 * Skip simulation before executing the transaction.
	 * Default: false (simulation enabled for safety)
	 *
	 * Set to true for:
	 * - Faster execution when you've already validated
	 * - E2E tests with local anvil
	 */
	skipSimulation?: boolean
}

// =============================================================================
// Viem Error Handling
// =============================================================================

/**
 * Extracted error information from a viem error
 */
export interface ViemErrorInfo {
	/** User-friendly error message */
	message: string
	/** Contract error name if available (e.g., "InsufficientBalance") */
	errorName?: string
	/** Contract error args if available */
	errorArgs?: readonly unknown[]
	/** Short viem error message */
	shortMessage?: string
	/** Full viem error details */
	details?: string
}

/**
 * Extract detailed error information from a viem error.
 * Preserves all context for Sentry while providing user-friendly messages.
 */
export function extractViemError(error: unknown): ViemErrorInfo {
	if (error instanceof BaseError) {
		// Walk the error chain to find ContractFunctionRevertedError
		const revertError = error.walk(e => e instanceof ContractFunctionRevertedError)

		if (revertError instanceof ContractFunctionRevertedError) {
			const errorName = revertError.data?.errorName
			const errorArgs = revertError.data?.args

			return {
				message: errorName ? `Contract reverted: ${errorName}` : "Contract reverted",
				errorName: errorName ?? undefined,
				errorArgs: errorArgs ?? undefined,
				shortMessage: error.shortMessage,
				details: error.details,
			}
		}

		return {
			message: error.shortMessage || error.message,
			shortMessage: error.shortMessage,
			details: error.details,
		}
	}

	if (error instanceof Error) {
		return { message: error.message }
	}

	return { message: "Unknown error" }
}

/**
 * Create a BreakoutError from a viem error with full context preserved.
 * The original error is set as `cause` for Sentry to capture the full chain.
 */
export function createExecutionError(
	error: unknown,
	context?: { market?: Address; functionName?: string; args?: unknown },
): ReturnType<typeof Errors.executionFailed> {
	const info = extractViemError(error)

	return Errors.executionFailed(
		info.message,
		{
			errorName: info.errorName,
			errorArgs: info.errorArgs,
			shortMessage: info.shortMessage,
			details: info.details,
			...context,
		},
		error,
	)
}

/**
 * Client type that supports contract reads.
 * Uses viem's base Client type for cross-package compatibility.
 * This is the same type that readContract from viem/actions accepts.
 */
export type ReadableClient = Client<Transport, Chain | undefined>

/**
 * Get connected account from wallet client.
 * @throws {BreakoutError} NO_WALLET_CONNECTED if no account
 */
export async function getAccount(client: WalletClient): Promise<Address> {
	const [account] = await client.getAddresses()
	if (!account) {
		throw Errors.noWalletConnected()
	}
	return account
}

// =============================================================================
// Retry Logic for RPC Resilience
// =============================================================================

/**
 * Retryable error types - these are transient and worth retrying
 */
function isRetryableError(error: unknown): boolean {
	if (error instanceof Error) {
		const message = error.message.toLowerCase()
		// Network/timeout errors
		if (message.includes("timeout") || message.includes("timed out")) {
			return true
		}
		if (message.includes("network") || message.includes("fetch failed")) {
			return true
		}
		if (message.includes("econnreset") || message.includes("econnrefused")) {
			return true
		}
		// RPC rate limiting
		if (message.includes("429") || message.includes("rate limit")) {
			return true
		}
		// RPC server errors
		if (message.includes("502") || message.includes("503") || message.includes("504")) {
			return true
		}
		if (message.includes("internal server error")) {
			return true
		}
	}
	return false
}

/**
 * Wraps an async function with exponential backoff retry logic.
 * Only retries transient errors (network, timeout, rate limit).
 */
async function withRetry<T>(
	fn: () => Promise<T>,
	options: { maxRetries?: number; baseDelayMs?: number } = {},
): Promise<T> {
	const { maxRetries = 3, baseDelayMs = 200 } = options
	let lastError: unknown

	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			return await fn()
		} catch (error) {
			lastError = error

			// Don't retry non-transient errors (contract reverts, invalid args, etc.)
			if (!isRetryableError(error)) {
				throw error
			}

			// Don't retry after max attempts
			if (attempt === maxRetries) {
				throw error
			}

			// Exponential backoff with jitter
			const delay = baseDelayMs * 2 ** attempt + Math.random() * 100
			await new Promise(resolve => setTimeout(resolve, delay))
		}
	}

	throw lastError
}

// =============================================================================
// Market Info
// =============================================================================

export interface RawMarketInfo {
	bucketCount: number
	liquidityParam: bigint
	feeBps: bigint
	openTimeSec: bigint // Unix seconds from blockchain
	cutoffTimeSec: bigint // Unix seconds from blockchain
	status: number
}

export async function readMarketInfo(
	client: ReadableClient,
	market: Address,
): Promise<RawMarketInfo> {
	const result = await withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "getMarketInfo",
		}),
	)

	return {
		bucketCount: result[0],
		liquidityParam: result[1],
		feeBps: result[2],
		openTimeSec: result[3],
		cutoffTimeSec: result[4],
		status: result[5],
	}
}

// =============================================================================
// Resolution
// =============================================================================

export interface RawResolution {
	winningOutcome: number
	dataUri: string
	nonce: number
	isResolved: boolean
	isFinalized: boolean
}

export async function readResolution(
	client: ReadableClient,
	market: Address,
): Promise<RawResolution> {
	const resolution = await withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "resolution",
		}),
	)

	return {
		winningOutcome: resolution[0],
		dataUri: resolution[1],
		nonce: resolution[2],
		isResolved: resolution[3],
		isFinalized: resolution[4],
	}
}

// =============================================================================
// Prices
// =============================================================================

export async function readPrices(
	client: ReadableClient,
	market: Address,
): Promise<readonly bigint[]> {
	return withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "getPrices",
		}),
	)
}

// =============================================================================
// User Position
// =============================================================================

export interface RawUserPosition {
	shares: readonly bigint[]
	claimable: bigint
	claimed: boolean
}

export async function readUserPosition(
	client: ReadableClient,
	market: Address,
	user: Address,
): Promise<RawUserPosition> {
	const result = await withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "getUserPosition",
			args: [user],
		}),
	)

	return {
		shares: result[0],
		claimable: result[1],
		claimed: result[2],
	}
}

// =============================================================================
// Quotes
// =============================================================================

export interface RawBuyQuote {
	cost: bigint
	fee: bigint
	total: bigint
}

export async function readQuoteBuy(
	client: ReadableClient,
	market: Address,
	bucket: number,
	shares: bigint,
): Promise<RawBuyQuote> {
	const result = await withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "quoteBuy",
			args: [bucket, shares],
		}),
	)

	return {
		cost: result[0],
		fee: result[1],
		total: result[2],
	}
}

export interface RawSellQuote {
	proceeds: bigint
	fee: bigint
	total: bigint
}

export async function readQuoteSell(
	client: ReadableClient,
	market: Address,
	bucket: number,
	shares: bigint,
): Promise<RawSellQuote> {
	const result = await withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "quoteSell",
			args: [bucket, shares],
		}),
	)

	return {
		proceeds: result[0],
		fee: result[1],
		total: result[2],
	}
}

// =============================================================================
// Other Reads
// =============================================================================

export async function readBucketCount(client: ReadableClient, market: Address): Promise<number> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "bucketCount",
	})
}

export async function readEmergencyPausedAt(
	client: ReadableClient,
	market: Address,
): Promise<bigint> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "emergencyPausedAt",
	})
}

export async function readMarketUsdc(client: ReadableClient, market: Address): Promise<Address> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "usdc",
	})
}

export async function readFeeBps(client: ReadableClient, market: Address): Promise<bigint> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "feeBps",
	})
}

export async function readAccruedProtocolFees(
	client: ReadableClient,
	market: Address,
): Promise<bigint> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "accruedProtocolFees",
	})
}

export async function readTotalLiquidity(client: ReadableClient, market: Address): Promise<bigint> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "totalLiquidity",
	})
}

export async function readUsedNonce(
	client: ReadableClient,
	market: Address,
	user: Address,
	nonce: bigint,
): Promise<boolean> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "usedNonces",
		args: [user, nonce],
	})
}

// =============================================================================
// Quantities and Liquidity (for LMSR calculations)
// =============================================================================

export async function readAllQuantities(
	client: ReadableClient,
	market: Address,
): Promise<readonly bigint[]> {
	return withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "getAllQuantities",
		}),
	)
}

export async function readLiquidityParam(client: ReadableClient, market: Address): Promise<bigint> {
	return withRetry(() =>
		readContract(client, {
			address: market,
			abi: breakoutMarketAbi,
			functionName: "liquidityParam",
		}),
	)
}

/**
 * Market state needed for LMSR quote calculations.
 * All values are in contract scale.
 */
export interface RawMarketQuoteState {
	/** Bucket count */
	bucketCount: number
	/** Fee in basis points */
	feeBps: number
	/** Liquidity parameter (raw scale) */
	liquidityParam: bigint
	/** Quantities per bucket (signed, raw scale) */
	quantities: readonly bigint[]
	/** Current prices (WAD scale 1e18) */
	prices: readonly bigint[]
}

/**
 * Fetch all market state needed for quote calculations in a single RPC call.
 * Uses getMarketInfo + getAllQuantities + getPrices via Promise.all.
 *
 * This is more efficient than separate calls and provides all data
 * needed for client-side LMSR calculations.
 */
export async function readMarketQuoteState(
	client: ReadableClient,
	market: Address,
): Promise<RawMarketQuoteState> {
	// Fetch all state in parallel (3 RPC calls batched by viem)
	const [marketInfo, quantities, prices] = await Promise.all([
		readMarketInfo(client, market),
		readAllQuantities(client, market),
		readPrices(client, market),
	])

	return {
		bucketCount: marketInfo.bucketCount,
		feeBps: Number(marketInfo.feeBps),
		liquidityParam: marketInfo.liquidityParam,
		quantities,
		prices,
	}
}

// =============================================================================
// Status Derivation
// =============================================================================

export function deriveMarketStatus(
	_contractStatus: number,
	openTimeSec: bigint,
	cutoffTimeSec: bigint,
	resolution: RawResolution,
	emergencyPausedAt: bigint,
): MarketStatus {
	const now = Date.now()

	// Check paused first
	if (emergencyPausedAt > 0n) {
		return { type: "paused", pausedAt: fromTimestamp(emergencyPausedAt) }
	}

	// Finalized
	if (resolution.isFinalized) {
		return { type: "finalized", winner: resolution.winningOutcome }
	}

	// Resolved (awaiting finalization)
	if (resolution.isResolved) {
		return {
			type: "resolved",
			winner: resolution.winningOutcome,
		}
	}

	// Closed (cutoff passed, not resolved)
	const cutoffMs = Number(cutoffTimeSec) * 1000
	if (now >= cutoffMs) {
		return { type: "closed" }
	}

	// Open (between open and cutoff)
	const openMs = Number(openTimeSec) * 1000
	if (now >= openMs) {
		return { type: "open", closesIn: cutoffMs - now }
	}

	// Pending (before open)
	return { type: "pending", opensIn: openMs - now }
}
